<template>
  <Maintenance v-if="featureMaintenance"/>
  <template v-else>
    <SkyHeader />
    <Header :content="header" />
    <router-view />
    <Footer :content="footer" />
    <Modal
      v-if="modalStore.isVisible"
      :content="modalStore.content"
      :action="closeModal"
    />
    <!-- Mobile-only sticky bottom install banner. Self-gates: on
         desktop it never renders; on mobile it renders once until
         the user dismisses it (localStorage). Lives at the root so
         it's visible on every page, not just the home. -->
    <MobileInstallBanner />
    <transition name="loading-fade">
      <LoadingScreen v-if="!isReady" />
    </transition>
  </template>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/index';
import { useStore } from '@/stores/index'
import config from '@/data/config'
import { gqlGeneric } from '@/gqls'
import Maintenance from '@/views/Maintenance.vue'
import Header from '@/views/Header.vue'
import Footer from '@/views/Footer.vue'
import SkyHeader from '@/components/Sections/SkyHeader.vue'
import Modal from '@/components/Text/Popup.vue'
import LoadingScreen from '@/components/Text/LoadingScreen.vue'
import MobileInstallBanner from '@/components/Sections/MobileInstallBanner.vue'
import { getLanguage } from '@/helpers'

const MIN_LOADING_MS = 1000   // minimum on-screen time for the loader
const MAX_LOADING_MS = 4000   // safety cap if data never resolves

export default {
  components: {
    Maintenance,
    SkyHeader,
    Header,
    Footer,
    Modal,
    LoadingScreen,
    MobileInstallBanner,
  },
  data() {
    return {
      featureMaintenance: config.featureMaintenance,
      header: null,
      footer: null,
      isReady: false,
      minTimeReached: false,
      dataReady: false,
    }
  },
  computed: {
    ...mapState(useStore, ['language'])
  },
  created() {
    const lang = getLanguage()
    this.setLanguage(lang)
  },
  mounted() {
    // Hold the loader on screen for at least MIN_LOADING_MS so very fast
    // loads don't produce a jarring flash.
    this._minTimer = setTimeout(() => {
      this.minTimeReached = true
      this.checkReady()
    }, MIN_LOADING_MS)

    // Safety: if Apollo never resolves (offline / API down), reveal anyway.
    this._maxTimer = setTimeout(() => {
      if (!this.isReady) this.isReady = true
    }, MAX_LOADING_MS)
  },
  beforeUnmount() {
    if (this._minTimer) clearTimeout(this._minTimer)
    if (this._maxTimer) clearTimeout(this._maxTimer)
  },
  setup() {
    const modalStore = useModalStore();
    return { modalStore };
  },
  methods: {
    ...mapActions(useStore, ['setLanguage']),
    closeModal() {
      const modalStore = useModalStore();
      modalStore.toggleModal()
    },
    checkReady() {
      if (this.minTimeReached && this.dataReady) {
        this.isReady = true
      }
    }
  },
  apollo: {
    content: {
      query: gqlGeneric,
      variables() {
        return {
          'language': this.language
        }
      },
      skip() {
        return this.featureMaintenance;
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.header = data?.header
          this.footer = data?.footer
          this.dataReady = true
          this.checkReady()
        }
      },
      error(error) {
        console.log(error)
        // Don't get stuck on the loader if the query errors out. let the
        // safety timeout handle reveal.
      }
    }
  }
}
</script>

<style lang="scss">
.banner+#header {
  margin-top: $banner-height;
}

.banner+#header.sticky {
  top: $banner-height;
}

#header+div {
  margin-top: $header-height;
}

/* Loading-screen fade-out. when isReady flips true the LoadingScreen
   unmounts via Vue <transition> with a smooth opacity wipe. */
.loading-fade-leave-active {
  transition: opacity 500ms ease-out;
}
.loading-fade-leave-to {
  opacity: 0;
}
</style>
